import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform, StyleSheet, View, type View as RNView } from "react-native";
import CustomText from "../../components/common/general/CustomText/CustomText";
import { colors, radii, spacing } from "../../styles/design-system";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import { CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import { Circle, CircleCheck } from "lucide-react";
import { FormattedObjective } from "../../graphql/generated/graphql";
import useResponsiveScreen from "../../hooks/useResponsiveScreen";
import { MotiView } from "moti";
import confetti from "canvas-confetti";

export type Objective = Pick<FormattedObjective, "id" | "objective" | "status" | "evalCriteria"> & {
  isCompleted?: boolean;
};

interface LearningGoalsContainerProps {
  objectives: Objective[];
}

const EXPANDED_WIDTH = 280;
const COLLAPSED_WIDTH = 56;
const CELEBRATION_DURATION_MS = 520;

const isObjectiveCompleted = (objective: Objective): boolean => {
  if (typeof objective.isCompleted === "boolean") {
    return objective.isCompleted;
  }

  const normalizedStatus = objective.status?.trim().toLowerCase();
  return normalizedStatus === "completed";
};

const LearningGoalsContainer: React.FC<LearningGoalsContainerProps> = ({ objectives }) => {
  const { isLarge, isXXLarge } = useResponsiveScreen();

  const [menuVisible, setMenuVisible] = useState(isXXLarge);
  const [celebratingObjectiveKey, setCelebratingObjectiveKey] = useState<string | null>(null);
  const previousStatusByObjectiveRef = useRef<Map<string, Objective["status"] | null>>(new Map());
  const celebrationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const objectiveElementByKeyRef = useRef<Map<string, HTMLElement>>(new Map());
  const getObjectiveKey = useCallback((objective: Objective) => objective.id ?? objective.objective, []);

  const activeObjectiveId = useMemo(() => {
    if (!objectives.length) {
      return null;
    }

    // Active objective:
    // - first objective that is NOT completed
    // - if all completed, fall back to the last objective
    const firstIncomplete = objectives.find((objective) => !isObjectiveCompleted(objective));

    const activeObjective = firstIncomplete ?? objectives[objectives.length - 1];

    return activeObjective?.id ?? null;
  }, [objectives]);

  const completeObjectivesCount = objectives.filter((objective) => isObjectiveCompleted(objective)).length;

  const triggerConfettiAnimation = useCallback((objectiveKey: string) => {
    if (Platform.OS !== "web") {
      return;
    }

    const element = objectiveElementByKeyRef.current.get(objectiveKey);
    if (!element) {
      return;
    }

    const rect = element.getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;

    confetti({
      particleCount: 30,
      spread: 60,
      origin: { x, y },
      colors: [colors.brand[500], colors.brand[300], colors.brand[100]],
      startVelocity: 20,
      gravity: 0.8,
      ticks: 100,
      scalar: 0.8,
    });
  }, []);

  const setObjectiveRowRef = useCallback(
    (objectiveKey: string) => (node: RNView | null) => {
      if (Platform.OS !== "web") {
        return;
      }

      if (!node) {
        objectiveElementByKeyRef.current.delete(objectiveKey);
        return;
      }

      const element = node as unknown as HTMLElement;
      if (typeof element.getBoundingClientRect === "function") {
        objectiveElementByKeyRef.current.set(objectiveKey, element);
      }
    },
    []
  );

  useEffect(() => {
    const nextStatusByObjective = new Map<string, Objective["status"] | null>();
    let newlyCompletedObjectiveKey: string | null = null;

    objectives.forEach((objective) => {
      const objectiveKey = getObjectiveKey(objective);
      const currentStatus = objective.status ?? null;
      const previousStatus = previousStatusByObjectiveRef.current.get(objectiveKey);
      const wasJustCompleted =
        previousStatus !== undefined &&
        previousStatus !== null &&
        previousStatus !== "completed" &&
        currentStatus === "completed";

      if (wasJustCompleted && !newlyCompletedObjectiveKey) {
        newlyCompletedObjectiveKey = objectiveKey;
      }

      nextStatusByObjective.set(objectiveKey, currentStatus);
    });
    previousStatusByObjectiveRef.current = nextStatusByObjective;

    if (newlyCompletedObjectiveKey) {
      setCelebratingObjectiveKey(newlyCompletedObjectiveKey);
      triggerConfettiAnimation(newlyCompletedObjectiveKey);

      if (celebrationTimeoutRef.current) {
        clearTimeout(celebrationTimeoutRef.current);
      }

      celebrationTimeoutRef.current = setTimeout(() => {
        setCelebratingObjectiveKey(null);
      }, CELEBRATION_DURATION_MS);
    } else if (celebratingObjectiveKey && !nextStatusByObjective.has(celebratingObjectiveKey)) {
      setCelebratingObjectiveKey(null);
    }
  }, [celebratingObjectiveKey, getObjectiveKey, objectives, triggerConfettiAnimation]);

  useEffect(() => {
    return () => {
      if (celebrationTimeoutRef.current) {
        clearTimeout(celebrationTimeoutRef.current);
      }
    };
  }, []);

  const handleExpand = () => setMenuVisible((prev) => !prev);

  return (
    <View
      style={[
        styles.learningGoalsContainer,
        isLarge && styles.learningGoalsContainerLarge,
        { width: menuVisible ? EXPANDED_WIDTH : COLLAPSED_WIDTH },
      ]}>
      {/* Header */}
      {menuVisible ? (
        <View style={styles.header}>
          <CustomText useTranslationText={false} text="Learning Goals" style={styles.title} weight="bold" size="s" />
          <CustomText
            text="Goals Count"
            interpolation={{ completedCount: completeObjectivesCount, total: objectives.length }}
            style={styles.subTitle}
          />

          <View style={styles.minimizeButtonContainer}>
            <CustomButton
              leftIcon={<CaretLeftIcon size={16} />}
              text=""
              accessibilityLabel="Collapse learning goals"
              styleType="secondaryLightened"
              style={styles.caretButton}
              onPress={handleExpand}
            />
          </View>
        </View>
      ) : (
        <View style={styles.maximizeButtonContainer}>
          <CustomButton
            leftIcon={<CaretRightIcon size={16} />}
            text=""
            accessibilityLabel="Expand learning goals"
            styleType="secondaryLightened"
            style={styles.caretButton}
            onPress={handleExpand}
          />
        </View>
      )}

      {/* Content */}
      <View style={menuVisible ? styles.contentExpanded : styles.contentCollapsed}>
        {objectives.map((objective) => {
          const objectiveKey = getObjectiveKey(objective);
          const isCelebrating = celebratingObjectiveKey === objectiveKey;
          const isSelected = activeObjectiveId === objective.id;
          const objectiveCriteria = (objective.evalCriteria ?? []).filter((criteria): criteria is string =>
            Boolean(criteria)
          );
          return (
            <View
              style={[
                styles.objectiveContainer,
                menuVisible ? styles.objectiveContainerExpanded : styles.objectiveContainerCollapsed,
                menuVisible && isSelected && styles.objectiveContainerActive,
              ]}
              key={objectiveKey}
              ref={setObjectiveRowRef(objectiveKey)}>
              <View style={[styles.objectiveIcon, menuVisible && styles.objectiveIconFull]}>
                {isObjectiveCompleted(objective) ? (
                  <MotiView
                    key={`${objectiveKey}-${isCelebrating ? "celebrating" : "steady"}`}
                    from={{ scale: isCelebrating ? 0.4 : 1 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "timing", duration: 500 }}>
                    <CircleCheck size={20} color={colors.success[600]} />
                  </MotiView>
                ) : (
                  <View
                    style={[
                      styles.circleHighlight,
                      isSelected && styles.circleHighlightActive,
                      isSelected && !menuVisible && styles.circleHighlightCollapsedActive,
                    ]}>
                    <Circle
                      size={20}
                      color={menuVisible ? colors.grey[400] : colors.grey[300]}
                      strokeWidth={isSelected && !menuVisible ? 2.5 : 2}
                    />
                  </View>
                )}
              </View>
              {menuVisible ? (
                <View style={styles.objectiveContent}>
                  <CustomText
                    text={objective.objective}
                    useTranslationText={false}
                    style={[styles.objectiveText, isObjectiveCompleted(objective) && styles.completedObjectiveText]}
                  />
                  {isSelected && objectiveCriteria.length > 0 ? (
                    <View style={styles.criteriaList}>
                      {objectiveCriteria.map((criteria, index) => (
                        <View
                          style={styles.criteriaItem}
                          key={`${objective.id ?? objective.objective}-criteria-${index}`}>
                          <CustomText text={criteria} useTranslationText={false} style={styles.criteriaText} />
                        </View>
                      ))}
                    </View>
                  ) : null}
                </View>
              ) : null}
            </View>
          );
        })}
        {!menuVisible ? (
          <CustomText
            text={`${completeObjectivesCount}/${objectives.length}`}
            style={styles.counterText}
            useTranslationText={false}
          />
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  learningGoalsContainer: {
    borderWidth: 1,
    borderColor: colors.grey[200],
    overflow: "hidden",
    borderRadius: radii.xl,
    backgroundColor: "#fff",
    shadowColor: "rgba(0, 0, 0, 0.08)",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 1,
    shadowRadius: 16,
    elevation: 4,
    position: "absolute",
    top: 8,
    left: 8,
    zIndex: 3,
    // @ts-expect-error - works for web
    height: "fit-content",
  },
  learningGoalsContainerLarge: {
    top: spacing.sm,
    left: spacing.sm,
  },
  header: {
    position: "relative",
    paddingTop: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  title: {
    lineHeight: 21,
    color: colors.grey[900],
  },
  subTitle: {
    fontSize: 10,
    lineHeight: 15,
    color: colors.grey[600],
  },
  minimizeButtonContainer: {
    position: "absolute",
    top: spacing.md,
    right: spacing.md,
    zIndex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  caretButton: {
    padding: 6,
  },
  maximizeButtonContainer: {
    alignItems: "center",
    justifyContent: "center",
    padding: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  contentCollapsed: {
    paddingTop: spacing.sm,
    paddingBottom: 12,
    gap: 12,
    alignItems: "center",
  },
  contentExpanded: {
    padding: spacing.md,
    gap: spacing.xs,
  },
  objectiveContainer: {
    flexDirection: "row",
    gap: spacing.xs,
    borderRadius: 16,
  },
  objectiveContainerExpanded: {
    alignItems: "flex-start",
    padding: 6,
  },
  objectiveContainerCollapsed: {
    width: "100%",
    justifyContent: "center",
  },
  objectiveContainerActive: {
    backgroundColor: colors.brand[50],
  },
  objectiveIcon: {
    borderRadius: "50%",
    alignItems: "center",
    justifyContent: "center",
  },
  objectiveIconFull: {
    marginTop: 4,
  },
  circleHighlight: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 2,
    borderColor: "transparent",
    backgroundColor: colors.white,
  },
  circleHighlightActive: {
    borderColor: colors.brand[200],
  },
  circleHighlightCollapsedActive: {
    borderWidth: 3,
  },
  objectiveText: {
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 20,
    color: colors.grey[900],
  },
  objectiveContent: {
    flex: 1,
  },
  criteriaList: {
    gap: 6,
    marginTop: 4,
    paddingRight: spacing.xs,
  },
  criteriaItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 6,
  },
  criteriaText: {
    flex: 1,
    fontSize: 11,
    lineHeight: 16,
    color: colors.grey[700],
  },
  completedObjectiveText: {
    color: colors.grey[500],
    opacity: 0.7,
    textDecorationLine: "line-through",
  },
  counterText: {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 18,
    color: colors.grey[900],
  },
});

export default LearningGoalsContainer;
